import { useState, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { UploadCloud, FileAudio, AlertCircle, Shield, CheckCircle2, X, Loader2, Play, Trash2, Sparkles, Mic, Users } from 'lucide-react'
import toast from 'react-hot-toast'
import { useAuthStore, useProcessingStore } from '@/store/auth'
import { recordingsService } from '@/services/firebase.service'
import { auth } from '@/lib/firebase'
import { AudioRecorder } from '@/components/AudioRecorder'

const ALLOWED_TYPES = ['audio/mpeg', 'audio/wav', 'audio/mp4', 'audio/x-m4a', 'audio/flac', 'audio/ogg', 'audio/webm', 'video/mp4', 'video/webm']
const ALLOWED_EXTENSIONS = ['.mp3', '.wav', '.m4a', '.mp4', '.flac', '.ogg', '.webm']
const MAX_SIZE = 500 * 1024 * 1024 // 500MB - Server handles compression

export function Upload() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { addProcessingFile, updateProcessingFile, processingFiles, clearCompleted } = useProcessingStore()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [numSpeakers, setNumSpeakers] = useState<number | null>(null) // null = auto-detect

  // Combine global processing files with local display
  const uploadQueue = processingFiles

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
  }

  const validateFile = (file: File): string | null => {
    const extension = '.' + file.name.split('.').pop()?.toLowerCase()
    if (!ALLOWED_EXTENSIONS.includes(extension) && !ALLOWED_TYPES.includes(file.type)) {
      return `Formato non supportato. Usa: ${ALLOWED_EXTENSIONS.join(', ')}`
    }
    if (file.size > MAX_SIZE) {
      return `File troppo grande (${formatFileSize(file.size)}). Max 500MB.`
    }
    return null
  }

  const uploadFile = useCallback(async (file: File) => {
    const firebaseUid = auth.currentUser?.uid
    if (!user || !firebaseUid) {
      toast.error('Devi essere autenticato per caricare file')
      return
    }

    const fileId = Date.now().toString() + Math.random().toString(36).substr(2, 9)

    // Add to global processing store
    addProcessingFile({
      id: fileId,
      recordingId: '', // Will be set after Firestore create
      fileName: file.name,
      fileSize: file.size,
      status: 'uploading',
      progress: 0,
      startedAt: new Date().toISOString(),
    })

    toast.loading(`Caricamento "${file.name}"...`, { id: fileId })

    try {
      console.log('=== STARTING UPLOAD ===')
      console.log('Firebase UID:', firebaseUid)
      console.log('File:', file.name, file.size)

      // 1. Create recording document in Firestore
      console.log('Step 1: Creating Firestore document...')
      const recordingId = await recordingsService.create(firebaseUid, {
        title: file.name.replace(/\.[^/.]+$/, ''),
        fileName: file.name,
        fileSize: file.size,
        duration: 0, // Will be updated after processing
        status: 'uploading',
        progress: 0,
        numSpeakers: numSpeakers, // null = auto-detect, number = exact count
      })

      // Update with recordingId so global hook can subscribe
      updateProcessingFile(fileId, { recordingId })
      console.log('Step 1 DONE. Recording ID:', recordingId)

      // 2. Upload audio file to Firebase Storage
      console.log('Step 2: Uploading to Firebase Storage...')
      const audioUrl = await recordingsService.uploadAudio(
        firebaseUid,
        recordingId,
        file,
        (progress) => {
          console.log('Upload progress:', progress, '%')
          updateProcessingFile(fileId, { progress })
        }
      )
      console.log('Step 2 DONE. Audio URL:', audioUrl)

      // 3. Update recording with audio URL - triggers Cloud Function
      console.log('Step 3: Updating Firestore with audio URL...')
      await recordingsService.update(recordingId, {
        status: 'processing',
        progress: 100,
        audioUrl,
      })
      console.log('Step 3 DONE. Processing started.')

      // Update local state - global hook will handle Firestore subscription
      updateProcessingFile(fileId, { status: 'processing', progress: 100 })
      toast.loading(`Elaborazione AI in corso (Whisper + GPT-4)...`, { id: fileId })

      // Note: Firestore subscription is now handled by useProcessingSubscriptions hook
      // which runs in the DashboardLayout and persists across page navigation

    } catch (error: any) {
      console.error('=== UPLOAD ERROR ===')
      console.error('Error:', error)
      console.error('Error code:', error?.code)
      console.error('Error message:', error?.message)
      console.error('Error serverResponse:', error?.serverResponse)
      const errorMsg = error?.code === 'storage/unauthorized'
        ? 'Permessi negati - verifica autenticazione'
        : error?.code === 'storage/canceled'
        ? 'Upload annullato'
        : error?.message || 'Errore upload'
      updateProcessingFile(fileId, { status: 'error', error: errorMsg })
      toast.error(`Errore: ${errorMsg}`, { id: fileId })
    }
  }, [user, addProcessingFile, updateProcessingFile])

  const handleFiles = useCallback((files: FileList | File[]) => {
    const fileArray = Array.from(files)

    fileArray.forEach(file => {
      const error = validateFile(file)
      if (error) {
        toast.error(`${file.name}: ${error}`)
        return
      }
      uploadFile(file)
    })
  }, [uploadFile])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    if (e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files)
    }
  }, [handleFiles])

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files)
      e.target.value = '' // Reset input
    }
  }, [handleFiles])

  const { removeProcessingFile } = useProcessingStore.getState()

  const removeFromQueue = useCallback((id: string) => {
    removeProcessingFile(id)
    toast.success('File rimosso dalla coda')
  }, [])

  const clearQueue = useCallback(() => {
    clearCompleted()
    toast.success('Cronologia svuotata')
  }, [clearCompleted])

  const openRecording = useCallback((recordingId: string) => {
    navigate(`/recordings/${recordingId}`)
  }, [navigate])

  const completedCount = uploadQueue.filter(f => f.status === 'completed').length
  const activeCount = uploadQueue.filter(f =>
    f.status === 'uploading' || f.status === 'processing' ||
    f.status === 'transcribing' || f.status === 'analyzing'
  ).length

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
         <div>
            <h2 className="text-3xl font-bold text-slate-900">Caricamento Audio</h2>
            <p className="text-slate-500 mt-2">Carica file audio per trascrizione e analisi AI.</p>
         </div>
         <div className="flex items-center gap-3">
           <span className="bg-emerald-100 text-emerald-800 text-xs font-bold px-3 py-1 rounded-full border border-emerald-200 flex items-center gap-1">
              <Shield className="h-3 w-3" /> Firebase Storage
           </span>
         </div>
      </div>

      {/* Upload Zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`bg-white rounded-2xl border-2 border-dashed transition-all p-12 text-center cursor-pointer relative overflow-hidden ${
          isDragging
            ? 'border-blue-500 bg-blue-50 shadow-[0_0_0_4px_rgba(59,130,246,0.2)]'
            : 'border-slate-300 hover:border-slate-500 hover:shadow-[0_0_0_4px_rgba(15,23,42,0.1)]'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={ALLOWED_EXTENSIONS.join(',')}
          multiple
          onChange={handleFileSelect}
          className="hidden"
        />

        <div className={`absolute inset-0 transition-opacity ${isDragging ? 'opacity-100 bg-blue-50/80' : 'opacity-0 bg-slate-50/50'}`} />

        <div className="relative z-10">
            <div className={`w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 transition-all duration-300 shadow-sm ${
              isDragging ? 'bg-blue-500 scale-110' : 'bg-slate-100 hover:bg-slate-900 hover:scale-110'
            }`}>
                <UploadCloud className={`h-10 w-10 transition-colors ${isDragging ? 'text-white' : 'text-slate-400 group-hover:text-white'}`} />
            </div>
            <h3 className="text-xl font-bold text-slate-900">
              {isDragging ? 'Rilascia per caricare' : 'Trascina qui i file audio'}
            </h3>
            <p className="text-sm text-slate-500 mt-2 mb-8 max-w-md mx-auto leading-relaxed">
              Supporta <strong>MP3, WAV, M4A, MP4, FLAC</strong> fino a <strong>500MB</strong>. Compressione automatica lato server.
            </p>
            <div className="flex justify-center gap-4">
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    fileInputRef.current?.click()
                  }}
                  className="bg-slate-900 hover:bg-slate-800 text-white px-8 py-3 rounded-xl font-medium shadow-lg shadow-slate-900/20 transition-all active:scale-95 flex items-center gap-2"
                >
                    <FileAudio className="h-4 w-4" /> Seleziona File
                </button>
            </div>
        </div>
      </div>

      {/* Speaker Count Selector */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 bg-purple-100 rounded-lg flex items-center justify-center">
            <Users className="h-5 w-5 text-purple-600" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">Numero di Speaker</h3>
            <p className="text-xs text-slate-500">Specifica quante persone parlano per una diarization piu precisa</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setNumSpeakers(null)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              numSpeakers === null
                ? 'bg-purple-600 text-white shadow-lg shadow-purple-600/30'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            Auto-detect
          </button>
          {[2, 3, 4, 5, 6, 7, 8].map(n => (
            <button
              key={n}
              onClick={() => setNumSpeakers(n)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all min-w-[48px] ${
                numSpeakers === n
                  ? 'bg-purple-600 text-white shadow-lg shadow-purple-600/30'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {n}
            </button>
          ))}
        </div>
        {numSpeakers !== null && (
          <p className="text-xs text-purple-600 mt-3 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3" />
            Il modello identifichera esattamente {numSpeakers} speaker diversi
          </p>
        )}
      </div>

      {/* Recording + Info Section */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Audio Recorder */}
        <AudioRecorder
          onRecordingComplete={(file) => {
            // Validate and upload the recorded file
            const error = validateFile(file)
            if (error) {
              toast.error(error)
              return
            }
            uploadFile(file)
          }}
          maxDuration={600} // 10 minutes max
        />

        {/* Info Cards */}
        <div className="space-y-4">
          {[
              { icon: Mic, title: "Registra dal Browser", desc: "Registra audio direttamente dal microfono del tuo dispositivo." },
              { icon: Shield, title: "Firebase Storage", desc: "File salvati in modo sicuro nel cloud." },
              { icon: Sparkles, title: "AI Processing", desc: "Trascrizione Whisper + Analisi GPT-4 automatica." }
          ].map((item, i) => (
              <div key={i} className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow flex items-start gap-3">
                  <item.icon className="h-5 w-5 text-slate-700 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-slate-900 text-sm">{item.title}</h4>
                    <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{item.desc}</p>
                  </div>
              </div>
          ))}
        </div>
      </div>

      {/* Upload Queue */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 bg-slate-50/80 border-b border-slate-100 flex justify-between items-center">
            <div className="flex items-center gap-4">
              <h3 className="font-semibold text-slate-800 text-sm">Coda di Upload</h3>
              {activeCount > 0 && (
                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium flex items-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" /> {activeCount} in corso
                </span>
              )}
              {completedCount > 0 && (
                <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium">
                  {completedCount} completati
                </span>
              )}
            </div>
            {uploadQueue.length > 0 && (
              <button
                onClick={clearQueue}
                className="text-xs text-slate-500 hover:text-red-600 flex items-center gap-1 transition-colors"
              >
                <Trash2 className="h-3 w-3" /> Svuota
              </button>
            )}
        </div>

        {uploadQueue.length === 0 ? (
          <div className="p-8 text-center text-slate-400">
            <UploadCloud className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Nessun file in coda. Trascina un file per iniziare.</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {uploadQueue.map((file) => (
              <div
                key={file.id}
                className={`p-4 flex items-center justify-between transition-colors ${
                  file.status === 'completed' && file.recordingId ? 'hover:bg-slate-50 cursor-pointer' : ''
                }`}
                onClick={() => file.status === 'completed' && file.recordingId && openRecording(file.recordingId)}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className={`h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    file.status === 'completed' ? 'bg-emerald-50 text-emerald-600' :
                    file.status === 'error' ? 'bg-red-50 text-red-500' :
                    file.status === 'transcribing' ? 'bg-purple-50 text-purple-600' :
                    file.status === 'analyzing' ? 'bg-emerald-50 text-emerald-600' :
                    'bg-blue-50 text-blue-600'
                  }`}>
                    {file.status === 'completed' ? <CheckCircle2 className="h-5 w-5" /> :
                     file.status === 'error' ? <AlertCircle className="h-5 w-5" /> :
                     (file.status === 'processing' || file.status === 'transcribing' || file.status === 'analyzing') ? <Loader2 className="h-5 w-5 animate-spin" /> :
                     <UploadCloud className="h-5 w-5" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{file.fileName}</p>
                    <div className="flex items-center gap-2">
                      <p className="text-xs text-slate-500">
                        {file.status === 'completed' ? 'Completato' :
                         file.status === 'error' ? file.error :
                         file.status === 'transcribing' ? 'Whisper in corso...' :
                         file.status === 'analyzing' ? 'GPT-4 in corso...' :
                         file.status === 'processing' ? 'In elaborazione...' :
                         `Caricamento ${Math.round(file.progress)}%`}
                        {' • '}{formatFileSize(file.fileSize)}
                      </p>
                    </div>
                    {(file.status === 'uploading') && (
                      <div className="mt-1.5 h-1 w-full bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-blue-500 rounded-full transition-all duration-300"
                          style={{ width: `${file.progress}%` }}
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 ml-4">
                  {file.status === 'completed' && file.recordingId && (
                    <span className="text-xs font-mono text-emerald-600 bg-emerald-50 px-2 py-1 rounded flex items-center gap-1">
                      <Play className="h-3 w-3" /> APRI
                    </span>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      removeFromQueue(file.id)
                    }}
                    className="text-slate-400 hover:text-red-500 p-1 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
